// src/pages/UploadPhotos.fixed.tsx
import React, { useState, useRef, useEffect, useCallback, ChangeEvent } from 'react';
import EditPhotoDialog from '../components/Photos/EditPhotoDialog';
import PhotoGalleryPopup from '../components/Photos/PhotoGalleryPopup';
import PhotoSharingModal from '../components/PhotoSharing/PhotoSharingModal';
import {
   Container,
   Typography,
   Box,
   Paper,
   Grid,
   Button,
   Card,
   CardMedia,
   CardContent,
   CardActions,
   IconButton,
   TextField,
   Tabs,
   Tab,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   CircularProgress,
   Snackbar,
   Alert,
   MenuItem,
   Select,
   FormControl,
   InputLabel,
   Chip,
   Divider,
   FormHelperText,
   DialogContentText,
   Checkbox,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import TabPanel from '../components/Photos/TabPanel';
import {
   PhotoLibrary as PhotoLibraryIcon,
   CalendarMonth as CalendarMonthIcon,
   Delete as DeleteIcon,
   Edit as EditIcon,
   CloudSync as CloudSyncIcon,
   Add as AddIcon,
   Close as CloseIcon,
   Image as ImageIcon,
   Search as SearchIcon,
   FilterList as FilterListIcon,
   LocationOn as LocationOnIcon,
   Share as ShareIcon,
   SelectAll as SelectAllIcon,
   ClearAll as ClearAllIcon,
   Download as DownloadIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { ref, uploadBytesResumable, getDownloadURL, uploadBytes } from 'firebase/storage';
import {
   collection,
   addDoc,
   Timestamp,
   query,
   where,
   getDocs,
   deleteDoc,
   doc,
   updateDoc,
   orderBy,
   getDoc,
} from 'firebase/firestore';
import { auth, storage, db, provider } from '../firebase/config';
// Removed dependencies on react-firebase-hooks
import PhotoCalendarView from '../components/Photos/PhotoCalendarView';
import PhotoSyncDialog from '../components/Photos/PhotoSyncDialog';
import { GoogleAuthProvider, signInWithPopup, signOut, UserCredential } from 'firebase/auth';
import { useUser } from '../contexts/UserContext';
import EXIF from 'exif-js';
import { get_all_events_and_shared_events, get_user_transformed_photos } from '../services';
import { get_all_trips_and_shared_trips } from '../services';
import { getImageTakenDate, objectURLToFile } from '../utils/imageUtils';
// Import action tracking utilities
import {
   trackUserAction,
   trackClick,
   trackContentAction,
   trackQuickError,
   ACTION_TYPES,
   ACTION_CATEGORIES,
   createMetadata,
   createPhotoMetadata,
   createPageMetadata,
} from '../utils/actionTracker';

// Type definitions
interface PhotoFile {
   id: string;
   file: File;
   preview: string;
   progress: number;
   error?: string;
   title: string;
   description: string;
   location?: {
      address?: string;
      name?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
   tags: string[];
   takenAt?: Date;
   tripId?: string;
   eventId?: string;
   tripName?: string;
   eventName?: string;
}

interface Photo {
   id: string;
   filename: string;
   url: string;
   thumbnailUrl: string;
   title: string;
   description: string;
   takenAt: Date;
   uploadedAt: Date;
   location?: {
      address?: string;
      name?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
   tags: string[];
   tripId?: string;
   eventId?: string;
   tripName?: string;
   eventName?: string;
}

interface Trip {
   id: string;
   title: string;
   startDate: Date;
   endDate: Date;
   location?: {
      address?: string;
      name?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
}

interface Event {
   id: string;
   title: string;
   startDate: Date;
   endDate: Date;
   type: string;
   location?: {
      address?: string;
      name?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
}

interface PhotoData {
   fileName: string;
   url: string;
   thumbnailUrl: string;
   title: string;
   description: string;
   takenAt: Timestamp;
   uploadedAt: Timestamp;
   createdAt: Timestamp;
   tags: string[];
   location?: {
      address?: string;
      name?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
   uploadedBy: string;
   tripId?: string;
   eventId?: string;
   tripName?: string;
   eventName?: string;
   userId: string;
}

const UploadPhotos: React.FC = () => {
   // Theme and responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   
   // State for basic UI
   const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
   const [isDeleting, setIsDeleting] = useState(false);
   const [currentTab, setCurrentTab] = useState(0);
   const [photoSyncDialogOpen, setPhotoSyncDialogOpen] = useState(false);
   const [syncTabValue, setSyncTabValue] = useState(0);
   // Photo dialog states
   const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [editDialogOpen, setEditDialogOpen] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<
      'success' | 'error' | 'info' | 'warning'
   >('info');

   // Photo gallery popup state
   const [galleryOpen, setGalleryOpen] = useState(false);
   const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

   // User authentication
   // const [user, setUser] = useState<any>(null);
   const { currentUser } = useUser();
   // Photo states
   const [photoFiles, setPhotoFiles] = useState<PhotoFile[]>([]);
   const [photos, setPhotos] = useState<Photo[]>([]);
   const [isUploading, setIsUploading] = useState(false);
   const [filteredPhotos, setFilteredPhotos] = useState<Photo[]>([]);
   // Photo selection states
   const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
   const [sharingModalOpen, setSharingModalOpen] = useState(false);
   // Calendar view state
   const [loading, setLoading] = useState(false);
   const [uploadProgress, setUploadProgress] = useState(0);
   const [bulkDownloading, setBulkDownloading] = useState(false);

   // Filter states
   const [searchTerm, setSearchTerm] = useState('');
   const [selectedTripId, setSelectedTripId] = useState<string>('');
   const [selectedEventId, setSelectedEventId] = useState<string>('');
   const [dateRange, setDateRange] = useState<{ start?: Date; end?: Date }>({});

   // Trips and events (for associating photos)
   const [trips, setTrips] = useState<Trip[]>([]);
   const [events, setEvents] = useState<Event[]>([]);
   const [isShowQrCode, setIsShowQrCode] = useState(false);
   const [qrCode, setQrCode] = useState<string>('');
   const [selectedPickerPhotos, setSelectedPickerPhotos] = useState<any[]>([]);
   const [photoUploadUrl, setPhotoUploadUrl] = useState<string>('');
   const [isSyncing, setIsSyncing] = useState(false);
   const [tripsLoading, setTripsLoading] = useState(false);
   const [eventsLoading, setEventsLoading] = useState(false);
   const [selectedFilterTripId, setSelectedFilterTripId] = useState<string>('');
   const [selectedFilterEventId, setSelectedFilterEventId] = useState<string>('');

   // Refs
   const fileInputRef = useRef<HTMLInputElement>(null);

   const handleDeleteCancel = () => {
      setDeleteDialogOpen(false);
   };

   // Photo selection handlers
   const togglePhotoSelection = (photoId: string) => {
      setSelectedPhotos((prev) => {
         const newSet = new Set(prev);
         if (newSet.has(photoId)) {
            newSet.delete(photoId);
         } else {
            newSet.add(photoId);
         }
         return newSet;
      });
   };

   const selectAllPhotos = () => {
      const allPhotoIds = new Set(filteredPhotos.map((photo) => photo.id));
      setSelectedPhotos(allPhotoIds);
   };

   const deselectAllPhotos = () => {
      setSelectedPhotos(new Set());
   };

   const handleSharePhotos = () => {
      if (selectedPhotos.size === 0) {
         setSnackbarMessage('Please select photos to share');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }
      setSharingModalOpen(true);
   };

   const handleBulkDownload = async () => {
      if (selectedPhotos.size === 0) {
         setSnackbarMessage('Please select photos to download');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setBulkDownloading(true);

      try {
         // Get selected photo objects before starting
         const photosToDownload = Array.from(selectedPhotos)
            .map((id) => photos.find((p) => p.id === id))
            .filter((p): p is Photo => p !== undefined);

         console.log('Starting bulk download of', photosToDownload.length, 'photos');

         setSnackbarMessage(`Starting download of ${photosToDownload.length} photo${photosToDownload.length > 1 ? 's' : ''}...`);
         setSnackbarSeverity('info');
         setSnackbarOpen(true);

         let successCount = 0;
         let errorCount = 0;

         // Download each photo using the same method as PhotoGroups
         for (let i = 0; i < photosToDownload.length; i++) {
            const photo = photosToDownload[i];
            
            // Update progress message
            if (photosToDownload.length > 1) {
               setSnackbarMessage(`Downloading photo ${i + 1} of ${photosToDownload.length}...`);
               setSnackbarSeverity('info');
               setSnackbarOpen(true);
            }
            
            try {
               console.log(`📥 Downloading photo ${i + 1}/${photosToDownload.length}:`, photo.title);

               const headers: Record<string, string> = {
                  'Content-Type': 'application/json',
               };

               const token = await auth.currentUser?.getIdToken();
               if (token) {
                  headers['Authorization'] = `Bearer ${token}`;
               }

               // Track download in background (non-blocking)
               const trackDownload = async () => {
                  try {
                     console.log('📊 [UploadPhotos] Tracking download for photo:', photo.id);
                     const trackResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/photos/${photo.id}/track-download`,
                        {
                           method: 'POST',
                           headers,
                        }
                     );

                     if (trackResponse.ok) {
                        console.log('✅ [UploadPhotos] Download tracked successfully');
                     } else {
                        console.error('❌ [UploadPhotos] Failed to track download:', trackResponse.status);
                     }
                  } catch (error) {
                     console.error('❌ [UploadPhotos] Could not track download:', error);
                  }
               };

               // Start tracking in background (don't wait for it)
               trackDownload();

               // Get signed download URL from backend
               const response = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/photos/${photo.id}/download`,
                  { headers }
               );

               if (!response.ok) {
                  let errorMessage = 'Failed to download photo';
                  try {
                     const errorData = await response.json();
                     errorMessage = errorData.message || errorMessage;
                  } catch (e) {
                     errorMessage = response.statusText || errorMessage;
                  }
                  throw new Error(errorMessage);
               }

               // Get filename from Content-Disposition header or use default
               const contentDisposition = response.headers.get('content-disposition');
               let fileName = `photo-${photo.id}.jpg`;
               if (contentDisposition) {
                  const match = contentDisposition.match(/filename="(.+)"/);
                  if (match && match[1]) {
                     fileName = match[1];
                  }
               }

               // Get the blob from streamed response
               const blob = await response.blob();
               console.log(`📥 Downloaded blob: ${blob.size} bytes, type: ${blob.type}`);

               // Create temporary download link and trigger immediate download
               const blobUrl = URL.createObjectURL(blob);
               const link = document.createElement('a');
               link.href = blobUrl;
               link.download = fileName;
               link.style.display = 'none';

               // Trigger download immediately
               document.body.appendChild(link);
               link.click();

               // Clean up immediately
               document.body.removeChild(link);
               URL.revokeObjectURL(blobUrl);

               successCount++;

               // Add delay between downloads to avoid browser blocking
               if (i < photosToDownload.length - 1) {
                  await new Promise((resolve) => setTimeout(resolve, 500));
               }
            } catch (error: any) {
               console.error(`❌ Error downloading photo ${photo.id}:`, error);
               errorCount++;
            }
         }

         console.log(`Download complete. Success: ${successCount}, Errors: ${errorCount}`);

         // Show success message
         if (errorCount === 0) {
            setSnackbarMessage(`Successfully downloaded ${successCount} photo${successCount > 1 ? 's' : ''}!`);
            setSnackbarSeverity('success');
         } else {
            setSnackbarMessage(`Downloaded ${successCount} photo${successCount > 1 ? 's' : ''}. ${errorCount} failed.`);
            setSnackbarSeverity('warning');
         }
         setSnackbarOpen(true);

         // Wait a bit before clearing selection so user can see the message
         setTimeout(() => {
            setSelectedPhotos(new Set());
            setBulkDownloading(false);
         }, 1000);

      } catch (error) {
         console.error('❌ Error during bulk download:', error);
         setSnackbarMessage('Error downloading photos');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         setBulkDownloading(false);
      }
   };

   const handleDeleteConfirm = async (id: string) => {
      const photoToDelete = photos.find((p) => p.id === id);

      try {
         setIsDeleting(true);

         // Track deletion attempt
         if (photoToDelete) {
            try {
               await trackUserAction({
                  actionType: ACTION_TYPES.DELETE_PHOTO,
                  category: ACTION_CATEGORIES.PHOTO,
                  description: `User deleted photo: ${photoToDelete.title}`,
                  targetId: id,
                  targetType: 'photo',
                  metadata: createPhotoMetadata('delete_photo', id, photoToDelete.title, {
                     source: 'upload_photos_page',
                     context: 'photo_management',
                     fileName: photoToDelete.filename,
                     associatedTripId: photoToDelete.tripId || undefined,
                     associatedEventId: photoToDelete.eventId || undefined,
                  }),
               });
            } catch (trackingError) {
               console.warn('Failed to track photo deletion:', trackingError);
            }
         }

         await deleteDoc(doc(db, 'photos', id));
         setSnackbarMessage('Photo deleted successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         // Track deletion error
         try {
            await trackQuickError(
               error instanceof Error ? error : new Error('Photo deletion failed'),
               'photo_deletion',
               {
                  photoId: id,
                  photoTitle: photoToDelete?.title || 'Unknown',
                  action: 'delete_photo_error',
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track deletion error:', trackingError);
         }

         setSnackbarMessage('Error deleting photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setIsDeleting(false);
         setDeleteDialogOpen(false);
      }
   };

   const handleEventChange = (event: any) => {
      setSelectedEventId(event.target.value);
   };

   const handleTripChange = (event: any) => {
      setSelectedTripId(event.target.value);
   };

   const loadTripsAndEvents = async () => {
      try {
         setTripsLoading(true);
         setEventsLoading(true);

         const trips = (await get_all_trips_and_shared_trips(
            currentUser?.uid || '',
            currentUser?.email || ''
         )) as any[];
         const events = (await get_all_events_and_shared_events(
            currentUser?.uid || '',
            currentUser?.email || ''
         )) as any[];

         const tripsData: any[] = [];
         const eventsData: any[] = [];

         for (const trip of trips) {
            tripsData.push({
               ...trip,
               id: trip.id,
               title: trip.title,
               startDate: trip.startDate,
               endDate: trip.endDate,
               location: {
                  address: trip.primaryDestination?.address || trip.location?.address || '',
                  name: trip.primaryDestination?.name || trip.location?.name || '',
                  coordinates: {
                     latitude:
                        trip.primaryDestination?.coordinates[0] ||
                        trip.location?.coordinates[0] ||
                        0,
                     longitude:
                        trip.primaryDestination?.coordinates[1] ||
                        trip.location?.coordinates[1] ||
                        0,
                  },
               },
            });
         }

         for (const event of events) {
            eventsData.push({
               ...event,
               id: event.id,
               title: event.title,
               startDate: event.startDate,
               endDate: event.endDate,
               type: event.type,
               location: {
                  address: event.location?.address || '',
                  name: event.location?.name || '',
                  coordinates: {
                     latitude: event.location?.coordinates[0] || 0,
                     longitude: event.location?.coordinates[1] || 0,
                  },
               },
            });
         }

         setTripsLoading(false);
         setEventsLoading(false);
         setTrips(tripsData);
         setEvents(eventsData);
      } catch (error) {
      } finally {
         setTripsLoading(false);
         setEventsLoading(false);
      }
   };

   const loadPhotos = async () => {
      if (!currentUser?.uid) return;

      setLoading(true);
      try {
         const transformedPhotos = (
            (await get_user_transformed_photos(currentUser.uid || '')) || []
         ).map((photo: any) => ({
            ...photo,
            id: photo.id,
            filename: photo.fileName || 'unknown',
            url: photo.url,
            thumbnailUrl: photo.thumbnailUrl || photo.url,
            title: photo.title || 'Untitled',
            description: photo.description || '',
            uploadedAt: photo.uploadedAt,
            takenAt: photo.createdAt.toDate() || null,
            tags: photo.tags || [],
            location: {
               address: '',
               name: '',
               coordinates: {
                  latitude: 0,
                  longitude: 0,
               },
            },
         }));

         const photosQuery = query(
            collection(db, 'photos'),
            where('userId', '==', currentUser?.uid),
            orderBy('uploadedAt', 'desc')
         );

         const photoSnapshot = await getDocs(photosQuery);

         const loadedPhotos: Photo[] = [];
         photoSnapshot.forEach((doc) => {
            const data = doc.data() as PhotoData;

            // Convert Firestore timestamps to Date objects
            const takenAt = data.takenAt ? data.takenAt.toDate() : new Date(data.takenAt);
            const uploadedAt =
               data.uploadedAt instanceof Timestamp
                  ? data.uploadedAt.toDate()
                  : new Date(data.uploadedAt);

            loadedPhotos.push({
               ...data,
               id: doc.id,
               filename: data.fileName,
               url: data.url,
               thumbnailUrl: data.thumbnailUrl || data.url, // Fallback to full url if no thumbnail
               title: data.title || 'Untitled',
               description: data.description || '',
               takenAt,
               uploadedAt,
               location: data.location,
               tags: data.tags || [],
            });
         });

         setPhotos([...loadedPhotos, ...transformedPhotos]);
      } catch (error) {
         console.log(error);
         setSnackbarMessage('Error loading photos. Please try again later.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setLoading(false);
      }
   };

   const createPickerSession = async (token: string) => {
      try {
         const response = await fetch('https://photospicker.googleapis.com/v1/sessions', {
            method: 'POST',
            headers: {
               Authorization: `Bearer ${token}`,
               'Content-Type': 'application/json',
            },
         });

         let data: any = {};

         if (response.status == 400) {
            data = await response.json();
            setSnackbarMessage(data?.message);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);

            setTimeout(() => {
               setSnackbarOpen(false);
               setSnackbarMessage('');
            }, 3000);
            return;
         } else if (response.status == 403) {
            data = await response.json();
            setSnackbarMessage(data?.message);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);

            setTimeout(() => {
               setSnackbarOpen(false);
               setSnackbarMessage('');
            }, 3000);
            return;
         }

         data = await response.json();

         //check for error

         if (!data.pickerUri) {
            return setSnackbarMessage('Failed load google photos session');
         }

         // You should get a URL to open the picker
         window.open(data.pickerUri, '_blank');

         setPhotoUploadUrl(data.pickerUri);

         setQrCode(
            `https://api.qrserver.com/v1/create-qr-code/?data=${data.pickerUri}&size=150x150`
         );

         setIsShowQrCode(true);
         const sessionId = await pollPickerSessionUntilMediaSet(token || '', data.id);
         const result = await fetchPickedMediaItems(token, sessionId);

         // setSelectedPickerPhotos((prev) => {
         //    return [...prev, ...result];
         // });
         setIsShowQrCode(false);
         setQrCode('');
         setPhotoUploadUrl('');
      } catch (error) {
         setIsShowQrCode(false);
         setQrCode('');
         setPhotoUploadUrl('');
      } finally {
         setIsSyncing(false);
      }
   };

   function parseDuration(duration: string): number {
      // Converts '5s' or '2.5s' to milliseconds
      return parseFloat(duration.replace('s', '')) * 1000;
   }

   async function pollPickerSessionUntilMediaSet(token: string, sessionId: string): Promise<any> {
      const getSession = async () => {
         const res = await fetch(`https://photospicker.googleapis.com/v1/sessions/${sessionId}`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
         });
         if (!res.ok) throw new Error(`Session fetch failed with status ${res.status}`);
         return res.json();
      };

      try {
         const initialData = await getSession();

         const pollIntervalMs = parseDuration(initialData.pollingConfig?.pollInterval || '5s');
         const timeoutMs = parseDuration(initialData.pollingConfig?.timeoutIn || '30s');

         const startTime = Date.now();

         return new Promise((resolve, reject) => {
            const poll = async () => {
               try {
                  const elapsed = Date.now() - startTime;

                  if (elapsed >= timeoutMs) {
                     return reject(new Error('Timeout reached before media was selected.'));
                  }

                  const data = await getSession();

                  if (data.mediaItemsSet === true) {
                     return resolve(sessionId); // ✅ Media items are ready
                  }

                  // Continue polling
                  setTimeout(poll, pollIntervalMs);
               } catch (error) {
                  reject(new Error('Polling error: ' + error));
               }
            };

            poll(); // Start polling
         });
      } catch (error) {
         throw new Error('Initial session fetch failed: ' + error);
      }
   }

   const fetchImage = async (token: string, baseUrl: string) => {
      try {
         const res = await fetch(baseUrl, {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });
         const blob = await res.blob();
         const objectUrl = URL.createObjectURL(blob);

         let metadata = {};
         // const onMetadataLoaded = async (metadata:any) => {
         //    metadata = metadata
         // }

         //   await getImageLocationAndTime(objectUrl, onMetadataLoaded)
         //    console.log('metadata', metadata)
         return {
            ...metadata,
            url: objectUrl,
            blob: blob,
         };
      } catch (err) {
         return null;
      }
   };

   async function fetchPickedMediaItems(token: string, sessionId: string): Promise<void> {
      let pageToken: string | null = null;

      try {
         do {
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/google-sync-photos?sessionId=${sessionId}&userId=${currentUser?.uid}&pageToken=${pageToken}&tripId=${selectedTripId ? selectedTripId : ''}&eventId=${selectedEventId ? selectedEventId : ''}
                  
                  `,
               {
                  method: 'GET',
                  headers: {
                     Authorization: `Bearer ${token}`,
                     'Content-Type': 'application/json',
                  },
               }
            );
            const data = (await response.json()) as any;
            pageToken = data?.nextPageToken || null;

            if (data?.photos.length > 0) {
               for (const photo of data?.photos) {
                  console.log('photo', photo);
                  await updateDoc(doc(db, 'photos', photo?.id), {
                     ...(selectedEventId
                        ? {
                             location: events.find((e) => e.id === selectedEventId)?.location,
                          }
                        : {}),

                     ...(selectedTripId
                        ? {
                             location: trips.find((t) => t.id === selectedTripId)?.location,
                          }
                        : {}),

                     ...(selectedEventId && { eventId: selectedEventId }),
                     ...(selectedTripId && { tripId: selectedTripId }),
                  });

                  setSelectedPickerPhotos((prev) => {
                     return [...prev, photo];
                  });
               }
            }
            await new Promise((resolve) => setTimeout(resolve, 100));
         } while (pageToken != null && pageToken != undefined);
      } catch (error) {
         console.log('error', error);
         throw error;
      }
   }

   // Google Photos sync handler
   const handleGooglePhotosSync = async () => {
      try {
         setSelectedPickerPhotos([]);
         setSnackbarMessage('Connecting to Google Photos...');
         setSnackbarSeverity('info');
         setSnackbarOpen(true);
         setIsSyncing(true);

         //force signout first before signing with email and the provider

         const result = (await signInWithPopup(auth, provider)) as UserCredential;

         const credential = GoogleAuthProvider.credentialFromResult(result);
         const token = credential?.accessToken;

         await createPickerSession(token || '');
      } catch (error: any) {
         let errorMessage = 'Unknown error occurred';
         if (error instanceof Error) {
            errorMessage = error.message;
         } else if (typeof error === 'string') {
            errorMessage = error;
         }

         setSnackbarMessage(`Error syncing with Google Photos: ${errorMessage}`);
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setIsSyncing(false);
      }
   };

   // iCloud sync handlers
   const handleICloudSync = useCallback(
      async (username: string, password: string, verificationCode?: string): Promise<void> => {
         try {
            if (!currentUser?.uid) {
               setSnackbarMessage('You must be logged in to sync with iCloud');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
               return;
            }

            const response = await fetch('/api/icloud/login', {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ username, password, verificationCode }),
            });

            const data = await response.json();

            if (data.success) {
               setSnackbarMessage('Successfully connected to iCloud');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               setPhotoSyncDialogOpen(false);
            } else if (data.requiresVerificationCode) {
               setSnackbarMessage('Verification code sent to your device');
               setSnackbarSeverity('info');
               setSnackbarOpen(true);
            } else {
               throw new Error(data.message || 'Failed to login to iCloud');
            }
         } catch (error) {
            console.error('iCloud sync error:', error);
            setSnackbarMessage('Error syncing with iCloud');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      },
      [currentUser?.uid]
   );

   const handleCloseSyncDialog = () => {
      setPhotoSyncDialogOpen(false);
   };

   const handleSyncTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setSyncTabValue(newValue);
   };

   const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
      const previousTab = currentTab;
      setCurrentTab(newValue);
      setSelectedPickerPhotos([]);

      // Track tab change
      const tabNames = ['Upload Photos', 'Manage Photos', 'Calendar View'];
      try {
         await trackUserAction({
            actionType: ACTION_TYPES.CLICK,
            category: ACTION_CATEGORIES.USER_INTERACTION,
            description: `User switched to ${tabNames[newValue]} tab`,
            metadata: createMetadata('tab_change', {
               source: 'upload_photos_page',
               context: 'navigation',
               fromTab: tabNames[previousTab] || 'Unknown',
               toTab: tabNames[newValue] || 'Unknown',
               fromTabIndex: previousTab,
               toTabIndex: newValue,
            }),
         });
      } catch (error) {
         console.warn('Failed to track tab change:', error);
      }
   };

   // Add file select handler
   const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) {
         return;
      }

      const newFiles = Array.from(event.target.files).map((file) => ({
         id: uuidv4(),
         file,
         preview: URL.createObjectURL(file),
         progress: 0,
         title: file.name.split('.')[0],
         description: '',
         tags: [],
      }));

      setPhotoFiles((prev) => [...prev, ...newFiles]);

      // Track photo selection action
      try {
         await trackUserAction({
            actionType: ACTION_TYPES.VIEW_CONTENT,
            category: ACTION_CATEGORIES.PHOTO,
            description: `User selected ${newFiles.length} photos for upload`,
            metadata: createPhotoMetadata(
               'photo_file_selection',
               newFiles[0].id,
               newFiles[0].file.name,
               {
                  source: 'upload_photos_page',
                  context: 'photo_upload',
                  photoCount: newFiles.length,
                  fileNames: newFiles.map((f) => f.file.name),
                  fileSizes: newFiles.map((f) => f.file.size),
                  fileTypes: newFiles.map((f) => f.file.type),
                  associatedTripId: selectedTripId || undefined,
                  associatedEventId: selectedEventId || undefined,
               }
            ),
         });
      } catch (error) {
         console.warn('Failed to track photo selection:', error);
      }

      // Reset the input to allow selecting the same file again
      if (fileInputRef.current) {
         fileInputRef.current.value = '';
      }
   };

   const handleRemovePhoto = async (id: string) => {
      const photoToRemove = photoFiles.find((p) => p.id === id);

      setPhotoFiles((prev) => prev.filter((photo) => photo.id !== id));

      // Track photo removal action
      if (photoToRemove) {
         try {
            await trackUserAction({
               actionType: ACTION_TYPES.DELETE_PHOTO,
               category: ACTION_CATEGORIES.PHOTO,
               description: `User removed photo from upload queue`,
               metadata: createPhotoMetadata(
                  'remove_from_upload_queue',
                  photoToRemove.id,
                  photoToRemove.file.name,
                  {
                     source: 'upload_photos_page',
                     context: 'photo_upload',
                     fileName: photoToRemove.file.name,
                     fileSize: photoToRemove.file.size,
                     fileType: photoToRemove.file.type,
                  }
               ),
            });
         } catch (error) {
            console.warn('Failed to track photo removal:', error);
         }
      }
   };

   const handleDeleteClick = (photo: Photo) => {
      setPhotoToDelete(photo);
      setDeleteDialogOpen(true);
   };

   const handleEditClick = (photo: Photo) => {
      setSelectedPhoto(photo);
      setEditDialogOpen(true);
   };

   const handleSaveEdit = async (photo: Photo, updatedData: Partial<Photo>) => {
      if (!currentUser?.uid || !photo.id) return;

      setLoading(true);
      const photoRef = doc(db, 'photos', photo.id);

      const updates: any = {
         tripId: updatedData.tripId || photo.tripId || '',
         eventId: updatedData.eventId || photo.eventId || '',
         title: updatedData.title || photo.title || '',
         description: updatedData.description || photo.description || '',
         location: updatedData.location || photo.location || '',
      };

      // Add timestamp for the update
      updates.updatedAt = Timestamp.now();

      // Track what fields were changed
      const changedFields: string[] = [];
      const changes: any = {};

      if (updatedData.tripId !== photo.tripId) {
         changedFields.push('tripId');
         changes.tripId = { from: photo.tripId, to: updatedData.tripId };
      }
      if (updatedData.eventId !== photo.eventId) {
         changedFields.push('eventId');
         changes.eventId = { from: photo.eventId, to: updatedData.eventId };
      }
      if (updatedData.title !== photo.title) {
         changedFields.push('title');
         changes.title = { from: photo.title, to: updatedData.title };
      }
      if (updatedData.description !== photo.description) {
         changedFields.push('description');
         changes.description = { from: photo.description, to: updatedData.description };
      }

      try {
         await updateDoc(photoRef, updates);

         // Track successful photo update
         try {
            await trackUserAction({
               actionType: 'update_photo', // Custom action type for photo updates
               category: ACTION_CATEGORIES.PHOTO,
               description: `Updated photo details: ${photo.title}`,
               targetId: photo.id,
               targetType: 'photo',
               metadata: createPhotoMetadata('update_photo', photo.id, photo.title, {
                  source: 'upload_photos_page',
                  context: 'photo_management',
                  fileName: photo.filename,
                  changedFields,
                  changes,
                  updateCount: changedFields.length,
               }),
            });
         } catch (trackingError) {
            console.warn('Failed to track photo update:', trackingError);
         }

         // Update the photo in the local state
         setPhotos((prev) =>
            prev.map((p) => {
               if (p.id === photo.id) {
                  return { ...p, ...updatedData };
               }
               return p;
            })
         );

         setSnackbarMessage('Photo details updated successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error updating photo:', error);

         // Track update error
         try {
            await trackQuickError(
               error instanceof Error ? error : new Error('Photo update failed'),
               'photo_update',
               {
                  photoId: photo.id,
                  photoTitle: photo.title,
                  changedFields,
                  action: 'update_photo_error',
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track update error:', trackingError);
         }

         setSnackbarMessage('Failed to update photo details');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setLoading(false);
         setEditDialogOpen(false);
      }
   };

   // State management for photo detail view
   const handlePhotoClick = async (photo: Photo) => {
      setSelectedPhoto(photo);
      // Find index of the photo in the photos array
      const photoIndex = photos.findIndex((p) => p.id === photo.id);
      if (photoIndex !== -1) {
         setSelectedPhotoIndex(photoIndex);
         setGalleryOpen(true);

         // Track photo view action
         try {
            await trackContentAction(ACTION_TYPES.VIEW_CONTENT, photo.id, 'photo', {
               photoTitle: photo.title,
               fileName: photo.filename,
               associatedTripId: photo.tripId || undefined,
               associatedEventId: photo.eventId || undefined,
               takenAt: photo.takenAt ? photo.takenAt.toISOString() : null,
               action: 'view_photo_detail',
            });
         } catch (trackingError) {
            console.warn('Failed to track photo view:', trackingError);
         }
      }
   };

   const handleCreateEventFromCalendar = async (
      startDate: Date,
      endDate: Date,
      photoIds: string[],
      title?: string,
      description?: string
   ): Promise<string> => {
      try {
         if (!currentUser || photoIds.length === 0) return '';

         setLoading(true);

         // Get the first photo to use as a cover image
         let coverImageUrl = null;
         if (photoIds.length > 0) {
            const firstPhotoId = photoIds[0];
            const photoDoc = await getDoc(doc(db, 'photos', firstPhotoId));
            if (photoDoc.exists()) {
               const photoData = photoDoc.data();
               coverImageUrl = photoData.url || photoData.thumbnailUrl;
            }
         }

         // Create the event in Firestore
         const eventData = {
            // Use the custom title and description if provided, otherwise use defaults
            title:
               title ||
               `Event from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`,
            description:
               description ||
               `Event created from calendar selection with ${photoIds.length} photos`,
            startDate,
            endDate,
            type: 'calendar-created',
            userId: currentUser.uid,
            owner: currentUser.uid, // Add owner field to match query criteria
            photoIds,
            createdAt: new Date(),
            coverImageUrl: coverImageUrl, // Add cover image URL
         };

         // Use backend API to create event with proper members array
         const { createApiUrl } = await import('../utils/apiConfig');
         const { getAuthHeaders } = await import('../utils/auth');
         const apiUrl = createApiUrl('/api/events');
         const headers = await getAuthHeaders();

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               title: eventData.title,
               description: eventData.description,
               startDate: eventData.startDate.toISOString(),
               endDate: eventData.endDate.toISOString(),
               eventType: 'other',
               isPublic: false,
               userId: currentUser.uid,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create event');
         }

         const eventRef = { id: result.eventId };

         // Update all selected photos with the event ID
         const batch = [];
         for (const photoId of photoIds) {
            const photoRef = doc(db, 'photos', photoId);
            batch.push(
               updateDoc(photoRef, {
                  eventId: eventRef.id,
                  eventName: eventData.title,
               })
            );
         }

         await Promise.all(batch);

         // Update local state
         const newEvent = {
            id: eventRef.id,
            ...eventData,
         };
         setEvents([newEvent, ...events]);

         // Update photo states
         const updatedPhotos = photos.map((photo) => {
            if (photoIds.includes(photo.id)) {
               return {
                  ...photo,
                  eventId: eventRef.id,
                  eventName: eventData.title,
               };
            }
            return photo;
         });
         setPhotos(updatedPhotos);

         setSnackbarSeverity('success');
         setSnackbarMessage(`Created event with ${photoIds.length} photos`);
         setSnackbarOpen(true);

         // Return the new event ID
         return eventRef.id;
      } catch (error) {
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to create event');
         setSnackbarOpen(true);
         return '';
      } finally {
         setLoading(false);
      }
   };

   // Handle creating a trip from calendar date selection
   const handleCreateTripFromCalendar = async (
      startDate: Date,
      endDate: Date,
      photoIds: string[],
      title?: string,
      description?: string
   ): Promise<string> => {
      try {
         if (!currentUser || photoIds.length === 0) return '';

         setLoading(true);

         // Get the first photo to use as a cover image
         let coverImageUrl = null;
         if (photoIds.length > 0) {
            const firstPhotoId = photoIds[0];
            const photoDoc = await getDoc(doc(db, 'photos', firstPhotoId));
            if (photoDoc.exists()) {
               const photoData = photoDoc.data();
               coverImageUrl = photoData.url || photoData.thumbnailUrl;
            }
         }

         // Create the trip in Firestore
         const tripData = {
            // Use the custom title and description if provided, otherwise use defaults
            title:
               title ||
               `Trip from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`,
            description:
               description || `Trip created from calendar selection with ${photoIds.length} photos`,
            startDate,
            endDate,
            type: 'calendar-created',
            userId: currentUser.uid,
            owner: currentUser.uid, // Add owner field to match query criteria
            photoIds,
            createdAt: new Date(),
            coverImageUrl: coverImageUrl, // Add cover image URL
         };

         // Use backend API to create trip with proper members array
         const { createApiUrl } = await import('../utils/apiConfig');
         const { getAuthHeaders } = await import('../utils/auth');
         const apiUrl = createApiUrl('/api/trips');
         const headers = await getAuthHeaders();

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               title: tripData.title,
               description: tripData.description,
               startDate: tripData.startDate.toISOString(),
               endDate: tripData.endDate.toISOString(),
               isPublic: false,
               userId: currentUser.uid,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create trip');
         }

         const tripRef = { id: result.tripId };

         // Update all selected photos with the trip ID
         const batch = [];
         for (const photoId of photoIds) {
            const photoRef = doc(db, 'photos', photoId);
            batch.push(
               updateDoc(photoRef, {
                  tripId: tripRef.id,
                  tripName: tripData.title,
               })
            );
         }

         await Promise.all(batch);

         // Update local state
         const newTrip = {
            id: tripRef.id,
            ...tripData,
         };
         setTrips([newTrip, ...trips]);

         // Update photo states
         const updatedPhotos = photos.map((photo) => {
            if (photoIds.includes(photo.id)) {
               return {
                  ...photo,
                  tripId: tripRef.id,
                  tripName: tripData.title,
               };
            }
            return photo;
         });
         setPhotos(updatedPhotos);

         setSnackbarSeverity('success');
         setSnackbarMessage(`Created trip with ${photoIds.length} photos`);
         setSnackbarOpen(true);

         // Return the new trip ID
         return tripRef.id;
      } catch (error) {
         console.error('Error creating trip from calendar:', error);
         setSnackbarSeverity('error');
         setSnackbarMessage('Failed to create trip');
         setSnackbarOpen(true);
         return '';
      } finally {
         setLoading(false);
      }
   };

   const handleCloseSnackbar = () => {
      setSnackbarOpen(false);
   };

   // const handleSyncPhotoUpload = async () => {
   //    try {
   //       if (!currentUser?.uid) {
   //          setSnackbarMessage('Please login to continue with action');
   //          setSnackbarSeverity('error');
   //          setSnackbarOpen(true);
   //       }

   //       if (selectedPickerPhotos.length == 0) {
   //          setSnackbarMessage('Please select photos to upload');
   //          setSnackbarSeverity('error');
   //          setSnackbarOpen(true);
   //       }

   //       setIsUploading(true);
   //       setUploadProgress(0);

   //       try {
   //          let completedUploads = 0;
   //          const totalFiles = selectedPickerPhotos.length;
   //          const uploadedPhotoIds: string[] = [];

   //          const updatedPhotoFiles: any[] = await Promise.all(
   //             selectedPickerPhotos.map(async (photo) => {
   //                return {
   //                   ...photo,
   //                   filename: photo.filename,
   //                   mimeType: photo.mimeType,
   //                   file: await objectURLToFile(photo.baseUrl, photo.filename),
   //                };
   //             })
   //          );

   //          for (let i = 0; i < totalFiles; i++) {
   //             const photo = updatedPhotoFiles[i];

   //             try {
   //                const storageRef = ref(
   //                   storage,
   //                   `photos/${currentUser?.uid}/${Date.now()}_${photo.filename}`
   //                );

   //                const uploadTask = uploadBytesResumable(storageRef, photo.file);

   //                // Monitor upload progress
   //                await new Promise<void>((resolve, reject) => {
   //                   uploadTask.on(
   //                      'state_changed',
   //                      (snapshot) => {
   //                         // Update individual photo progress
   //                         const progress = Math.round(
   //                            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
   //                         );

   //                         updatedPhotoFiles[i] = { ...photo, progress };
   //                         setSelectedPickerPhotos((prev: any): any[] => {
   //                            return prev.map((p: any) => {
   //                               if (p.id === photo.id) {
   //                                  return { ...p, progress };
   //                               }
   //                               return p;
   //                            });
   //                         });
   //                         setPhotoFiles([...updatedPhotoFiles]);

   //                         // Calculate overall progress
   //                         const overallProgress = Math.round(
   //                            (completedUploads / totalFiles) * 100 + progress / totalFiles
   //                         );
   //                         setUploadProgress(overallProgress);
   //                      },
   //                      (error) => {
   //                         // Handle errors
   //                         updatedPhotoFiles[i] = { ...photo, error: 'Upload failed' };
   //                         setPhotoFiles([...updatedPhotoFiles]);

   //                         reject(error);
   //                      },
   //                      async () => {
   //                         // Get download URL
   //                         const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

   //                         const photoDoc = await addDoc(collection(db, 'photos'), {
   //                            fileName: photo.filename,
   //                            url: downloadURL,
   //                            thumbnailUrl: downloadURL, // Use same URL for thumbnail in this implementation
   //                            title: photo.title || photo.filename.split('.')[0],
   //                            description: photo.description || '',
   //                            takenAt: Timestamp.fromDate(new Date(photo?.takenAt)),
   //                            uploadedAt: Timestamp.now(),
   //                            createdAt: Timestamp.now(),
   //                            tags: photo.tags || [],
   //                            uploadedBy: currentUser?.uid,
   //                            userId: currentUser?.uid,
   //                            ...(selectedEventId
   //                               ? {
   //                                    location: events.find((e) => e.id === selectedEventId)
   //                                       ?.location,
   //                                 }
   //                               : photo?.location
   //                                 ? { location: photo?.location }
   //                                 : {}),

   //                            ...(selectedTripId
   //                               ? {
   //                                    location: trips.find((t) => t.id === selectedTripId)
   //                                       ?.location,
   //                                 }
   //                               : photo?.location
   //                                 ? { location: photo?.location }
   //                                 : {}),

   //                            ...(selectedEventId && { eventId: selectedEventId }),
   //                            ...(selectedTripId && { tripId: selectedTripId }),
   //                         });

   //                         // Update the completed count
   //                         completedUploads++;

   //                         // Add to list of uploaded photo IDs
   //                         uploadedPhotoIds.push(photoDoc.id);

   //                         // Mark as successfully uploaded
   //                         updatedPhotoFiles[i] = { ...photo, progress: 100 };
   //                         setPhotoFiles([...updatedPhotoFiles]);

   //                         // Resolve the promise
   //                         resolve();
   //                      }
   //                   );
   //                });
   //                handleCloseSyncDialog();
   //             } catch (error) {
   //                // Mark the photo as errored
   //                updatedPhotoFiles[i] = { ...photo, error: 'Processing failed' };
   //                setPhotoFiles([...updatedPhotoFiles]);
   //             }
   //          }

   //          // All uploads completed
   //          setIsUploading(false);
   //          setUploadProgress(100);

   //          // Show success message
   //          setSnackbarMessage(`Successfully uploaded ${completedUploads} photos`);
   //          setSnackbarSeverity('success');
   //          setSnackbarOpen(true);

   //          // Clear the uploads after a short delay to show completion
   //          setTimeout(() => {
   //             setPhotoFiles([]);
   //             // Reload photos to include the newly uploaded ones
   //          }, 2000);
   //       } catch (error) {
   //          setIsUploading(false);

   //          setSnackbarMessage('Error uploading photos. Please try again.');
   //          setSnackbarSeverity('error');
   //          setSnackbarOpen(true);
   //       }
   //    } catch (error) {}
   // };

   const handleUploadPhotos = async () => {
      if (!currentUser?.uid || photoFiles.length === 0) {
         setSnackbarMessage('Please select photos to upload');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      // Track upload start
      const uploadStartTime = Date.now();
      let trackingMetadata = {
         photoCount: photoFiles.length,
         associatedTripId: selectedTripId || undefined,
         associatedEventId: selectedEventId || undefined,
         totalFileSize: photoFiles.reduce((sum, file) => sum + file.file.size, 0),
         fileTypes: Array.from(new Set(photoFiles.map((f) => f.file.type))),
         startTime: uploadStartTime,
      };

      try {
         await trackUserAction({
            actionType: ACTION_TYPES.UPLOAD_PHOTO,
            category: ACTION_CATEGORIES.PHOTO,
            description: `Started uploading ${photoFiles.length} photos`,
            metadata: createMetadata('upload_start', {
               source: 'upload_photos_page',
               context: 'photo_upload',
               ...trackingMetadata,
            }),
         });
      } catch (error) {
         console.warn('Failed to track upload start:', error);
      }

      setIsUploading(true);
      setUploadProgress(0);

      try {
         let completedUploads = 0;
         const totalFiles = photoFiles.length;
         const uploadedPhotoIds: string[] = [];
         const failedUploads: string[] = [];

         // Create a new array for immutable updates
         const updatedPhotoFiles = [...photoFiles];

         // Process each photo in sequence
         for (let i = 0; i < totalFiles; i++) {
            const photo = updatedPhotoFiles[i];
            const metadata = await getImageTakenDate(photo.file);

            try {
               // Create a storage reference
               const filePath = `photos/${currentUser?.uid}/${Date.now()}_${photo.file.name}`;
               const storageRef = ref(storage, filePath);

               // Create an upload task
               const uploadTask = uploadBytesResumable(storageRef, photo.file);

               // Monitor upload progress
               await new Promise<void>((resolve, reject) => {
                  uploadTask.on(
                     'state_changed',
                     (snapshot) => {
                        // Update individual photo progress
                        const progress = Math.round(
                           (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                        );
                        updatedPhotoFiles[i] = { ...photo, progress };
                        setPhotoFiles([...updatedPhotoFiles]);

                        // Calculate overall progress
                        const overallProgress = Math.round(
                           (completedUploads / totalFiles) * 100 + progress / totalFiles
                        );
                        setUploadProgress(overallProgress);
                     },
                     (error) => {
                        // Handle errors
                        updatedPhotoFiles[i] = { ...photo, error: 'Upload failed' };
                        setPhotoFiles([...updatedPhotoFiles]);
                        failedUploads.push(photo.file.name);

                        reject(error);
                     },
                     async () => {
                        // Get download URL
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                        // Store photo metadata in Firestore
                        const photoDoc = await addDoc(collection(db, 'photos'), {
                           fileName: photo.file.name,
                           url: downloadURL,
                           filePath: filePath,
                           thumbnailUrl: downloadURL, // Use same URL for thumbnail in this implementation
                           title: photo.title || photo.file.name.split('.')[0],
                           description: photo.description || '',
                           takenAt: metadata.takenDate
                              ? Timestamp.fromDate(metadata.takenDate).toDate()
                              : null,
                           uploadedAt: Timestamp.now(),
                           createdAt: Timestamp.now(),
                           tags: photo.tags || [],
                           uploadedBy: currentUser?.uid,
                           userId: currentUser?.uid,

                           ...(selectedEventId
                              ? {
                                   location: events.find((e) => e.id === selectedEventId)?.location,
                                }
                              : photo?.location
                                ? { location: photo?.location }
                                : {}),

                           ...(selectedTripId
                              ? {
                                   location: trips.find((t) => t.id === selectedTripId)?.location,
                                }
                              : photo?.location
                                ? { location: photo?.location }
                                : {}),

                           ...(selectedEventId && { eventId: selectedEventId }),
                           ...(selectedTripId && { tripId: selectedTripId }),
                        });

                        // Update the completed count
                        completedUploads++;

                        // Add to list of uploaded photo IDs
                        uploadedPhotoIds.push(photoDoc.id);

                        // Mark as successfully uploaded
                        updatedPhotoFiles[i] = { ...photo, progress: 100 };
                        setPhotoFiles([...updatedPhotoFiles]);

                        // Resolve the promise
                        resolve();
                     }
                  );
               });
            } catch (error) {
               console.log(error, 'error....');
               // Mark the photo as errored
               updatedPhotoFiles[i] = { ...photo, error: 'Processing failed' };
               setPhotoFiles([...updatedPhotoFiles]);
               failedUploads.push(photo.file.name);
            }
         }

         // All uploads completed
         setIsUploading(false);
         setUploadProgress(100);

         // Track upload completion
         const uploadEndTime = Date.now();
         const uploadDuration = (uploadEndTime - uploadStartTime) / 1000; // in seconds

         try {
            await trackUserAction({
               actionType: ACTION_TYPES.UPLOAD_PHOTO,
               category: ACTION_CATEGORIES.PHOTO,
               description: `Successfully uploaded ${completedUploads} of ${totalFiles} photos`,
               value: completedUploads,
               metadata: createMetadata('upload_complete', {
                  source: 'upload_photos_page',
                  context: 'photo_upload',
                  ...trackingMetadata,
                  successfulUploads: completedUploads,
                  failedUploads: failedUploads.length,
                  uploadDuration,
                  uploadedPhotoIds,
                  failedFileNames: failedUploads,
                  endTime: uploadEndTime,
                  averageFileSize: trackingMetadata.totalFileSize / totalFiles,
                  uploadSpeed: trackingMetadata.totalFileSize / uploadDuration, // bytes per second
               }),
            });
         } catch (error) {
            console.warn('Failed to track upload completion:', error);
         }

         // Show success message
         setSnackbarMessage(`Successfully uploaded ${completedUploads} photos`);
         setSnackbarSeverity('success');
         setSnackbarOpen(true);

         // Clear the uploads after a short delay to show completion
         setTimeout(() => {
            setPhotoFiles([]);
            // Reload photos to include the newly uploaded ones
         }, 2000);
      } catch (error) {
         setIsUploading(false);

         // Track upload error
         try {
            await trackQuickError(
               error instanceof Error ? error : new Error('Upload failed'),
               'photo_upload',
               {
                  ...trackingMetadata,
                  action: 'upload_error',
                  uploadDuration: (Date.now() - uploadStartTime) / 1000,
               }
            );
         } catch (trackingError) {
            console.warn('Failed to track upload error:', trackingError);
         }

         setSnackbarMessage('Error uploading photos. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   useEffect(() => {
      loadTripsAndEvents();
      loadPhotos();

      // Track page visit
      const trackPageVisit = async () => {
         try {
            await trackUserAction({
               actionType: ACTION_TYPES.PAGE_VIEW,
               category: ACTION_CATEGORIES.NAVIGATION,
               description: 'User visited Photo Upload page',
               metadata: createMetadata('page_visit', {
                  source: 'upload_photos_page',
                  context: 'navigation',
                  page: 'photo_upload',
                  currentTab: currentTab,
                  hasSelectedTrip: !!selectedTripId,
                  hasSelectedEvent: !!selectedEventId,
               }),
            });
         } catch (error) {
            console.warn('Failed to track page visit:', error);
         }
      };

      trackPageVisit();
   }, []);

   useEffect(() => {
      if (selectedFilterTripId) {
         setFilteredPhotos(photos.filter((photo) => photo.tripId === selectedFilterTripId));
      } else if (selectedFilterEventId) {
         setFilteredPhotos(photos.filter((photo) => photo.eventId === selectedFilterEventId));
      } else {
         setFilteredPhotos(photos);
      }
   }, [selectedFilterTripId, selectedFilterEventId]);

   useEffect(() => {
      if (searchTerm) {
         setFilteredPhotos(
            photos.filter(
               (photo) =>
                  photo?.title?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
                  photo?.filename?.toLowerCase()?.includes(searchTerm.toLowerCase())
            )
         );
      } else {
         setFilteredPhotos(photos);
      }
   }, [searchTerm]);

   useEffect(() => {
      loadPhotos();
   }, [isDeleting, deleteDialogOpen, isUploading, currentTab, editDialogOpen]);

   return (
         <Box
            sx={{
               width: '100%',
               maxWidth: isMobile ? '100vw' : '1200px',
               margin: '0 auto',
               overflowX: 'hidden',
               overflowY: 'auto',
               boxSizing: 'border-box',
               py: isMobile ? 2 : 4,
               px: isMobile ? 0.5 : 3,
            }}
         >
         <Typography
            variant='h5'
            gutterBottom
            component='div'
            sx={{
               px: isMobile ? 0.5 : 0,
               fontSize: isMobile ? '1.25rem' : '1.5rem',
            }}
         >
            Photo Management
         </Typography>

         {/* Tabs Navigation */}
         <Box
            sx={{
               borderBottom: 1,
               borderColor: 'divider',
               width: '100%',
               maxWidth: '100%',
               mb: 3,
               overflow: 'hidden',
            }}
         >
            <Tabs
               value={currentTab}
               onChange={handleTabChange}
               aria-label='photo tabs'
               variant='scrollable'
               scrollButtons='auto'
               allowScrollButtonsMobile
               sx={{
                  '& .MuiTabs-scroller': {
                     overflow: 'auto !important',
                  },
                  '& .MuiTab-root': {
                     minWidth: isMobile ? 'auto' : 'auto',
                     fontSize: isMobile ? '0.8rem' : '0.875rem',
                     padding: isMobile ? '12px 16px' : '12px 16px',
                     textTransform: 'none',
                     fontWeight: 600,
                  },
                  '& .MuiTabs-indicator': {
                     height: 3,
                     borderRadius: '3px 3px 0 0',
                  },
               }}
            >
               <Tab label='Manage Photos' icon={<PhotoLibraryIcon />} iconPosition='start' />
               <Tab label='Calendar View' icon={<CalendarMonthIcon />} iconPosition='start' />
            </Tabs>
         </Box>

         {/* Manage Photos Tab */}
         <TabPanel value={currentTab} index={0}>
            {/* Search and Filter Controls */}
            <Box
               sx={{
                  mb: 3,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
               }}
            >
                  <Grid
                     container
                     spacing={2}
                     alignItems='center'
                     sx={{
                        width: '100%',
                        m: 0,
                        boxSizing: 'border-box',
                     }}
                  >
                     <Grid item xs={12} md={4}>
                        <TextField
                           fullWidth
                           label='Search Photos'
                           variant='outlined'
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           InputProps={{
                              startAdornment: <SearchIcon color='action' sx={{ mr: 1 }} />,
                           }}
                           placeholder='Search by title, description, location...'
                        />
                     </Grid>
                     <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant='outlined'>
                           <InputLabel>Filter by Trip</InputLabel>
                           <Select
                              disabled={selectedFilterEventId ? true : false}
                              value={selectedFilterTripId}
                              label='Filter by Trip'
                              onChange={(e) => {
                                 setSelectedFilterTripId(e.target.value as string);
                                 setSelectedFilterEventId('');
                              }}
                           >
                              <MenuItem value=''>All Trips</MenuItem>
                              {trips.map((trip) => (
                                 <MenuItem key={trip.id} value={trip.id}>
                                    {trip.title}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>
                     <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant='outlined'>
                           <InputLabel>Filter by Event</InputLabel>
                           <Select
                              disabled={selectedFilterTripId ? true : false}
                              value={selectedFilterEventId}
                              label='Filter by Event'
                              onChange={(e) => {
                                 setSelectedFilterEventId(e.target.value as string);
                                 setSelectedFilterTripId('');
                              }}
                           >
                              <MenuItem value=''>All Events</MenuItem>
                              {events.map((event) => (
                                 <MenuItem key={event.id} value={event.id}>
                                    {event.title}
                                 </MenuItem>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>
                  </Grid>
               </Box>

               {/* Photo Selection Controls */}
               {(photos.length > 0 || filteredPhotos.length > 0) && (
                  <Box
                     sx={{
                        mb: 2,
                        p: isMobile ? 1.5 : 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                     }}
                  >
                     {/* Photo count text */}
                     <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: isMobile ? 1.5 : 0 }}
                     >
                        {selectedPhotos.size} of{' '}
                        {(filteredPhotos.length > 0 ? filteredPhotos : photos).length} photos
                        selected
                     </Typography>

                     {/* Horizontally scrollable buttons */}
                     <Box
                        sx={{
                           display: 'flex',
                           gap: 1.5,
                           overflowX: 'auto',
                           overflowY: 'hidden',
                           WebkitOverflowScrolling: 'touch',
                           scrollbarWidth: 'none',
                           msOverflowStyle: 'none',
                           '&::-webkit-scrollbar': {
                              display: 'none',
                           },
                           pb: isMobile ? 0.5 : 0,
                        }}
                     >
                        <Button
                           size='small'
                           startIcon={<SelectAllIcon />}
                           onClick={selectAllPhotos}
                           disabled={
                              selectedPhotos.size ===
                              (filteredPhotos.length > 0 ? filteredPhotos : photos).length
                           }
                           sx={{
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                           }}
                        >
                           Select All
                        </Button>
                        <Button
                           size='small'
                           startIcon={<ClearAllIcon />}
                           onClick={deselectAllPhotos}
                           disabled={selectedPhotos.size === 0}
                           sx={{
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                           }}
                        >
                           Deselect All
                        </Button>
                        <Button
                           variant='contained'
                           startIcon={<ShareIcon sx={{ color: 'white' }} />}
                           onClick={handleSharePhotos}
                           disabled={selectedPhotos.size === 0}
                           sx={{
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                              color: 'white !important',
                              '& .MuiButton-startIcon': {
                                 color: 'white',
                              },
                              '&:hover': {
                                 color: 'white !important',
                              },
                           }}
                        >
                           Share Selected ({selectedPhotos.size})
                        </Button>
                        <Button
                           variant='contained'
                           color='primary'
                           startIcon={
                              bulkDownloading ? (
                                 <CircularProgress size={16} sx={{ color: 'white' }} />
                              ) : (
                                 <DownloadIcon sx={{ color: 'white' }} />
                              )
                           }
                           onClick={handleBulkDownload}
                           disabled={selectedPhotos.size === 0 || bulkDownloading}
                           sx={{
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                              color: 'white !important',
                              '& .MuiButton-startIcon': {
                                 color: 'white',
                              },
                              '&:hover': {
                                 color: 'white !important',
                              },
                           }}
                        >
                           {bulkDownloading
                              ? 'Downloading...'
                              : `Download Selected (${selectedPhotos.size})`}
                        </Button>
                     </Box>
                  </Box>
               )}

               {/* Photo Gallery Grid */}
               {photos.length > 0 || filteredPhotos.length > 0 ? (
                  <Grid container spacing={isMobile ? 1 : 2}>
                     {(filteredPhotos.length > 0 ? filteredPhotos : photos).map((photo) => (
                        <Grid item xs={6} sm={6} md={4} lg={3} key={photo.id}>
                           <Card sx={{ height: '100%', position: 'relative' }}>
                              <CardMedia
                                 component='img'
                                 height={isMobile ? '120' : '140'}
                                 image={photo?.thumbnailUrl}
                                 alt={photo?.title}
                                 onClick={() => handlePhotoClick(photo)}
                                 sx={{ cursor: 'pointer' }}
                                 onError={(e) => {
                                    // This ensures images still display something even if they fail to load
                                    (e.target as HTMLImageElement).src =
                                       'https://via.placeholder.com/140?text=Image+Error';
                                 }}
                              />

                              {/* Selection checkbox overlay */}
                              <Box
                                 position='absolute'
                                 top={8}
                                 left={8}
                                 bgcolor={
                                    selectedPhotos.has(photo.id) ? '#1976d2' : 'rgba(0,0,0,0.6)'
                                 }
                                 borderRadius='50%'
                                 p={0.3}
                                 sx={{
                                    transition: 'all 0.2s ease',
                                    border: selectedPhotos.has(photo.id)
                                       ? '2px solid white'
                                       : '2px solid transparent',
                                    zIndex: 10,
                                 }}
                              >
                                 <Checkbox
                                    checked={selectedPhotos.has(photo.id)}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                       e.stopPropagation();
                                       togglePhotoSelection(photo.id);
                                    }}
                                    size='small'
                                    sx={{
                                       color: 'white',
                                       p: 0,
                                       '&.Mui-checked': {
                                          color: 'white',
                                       },
                                    }}
                                 />
                              </Box>
                              <CardContent sx={{ p: isMobile ? 1 : 2, pb: isMobile ? '8px !important' : 2 }}>
                                 {photo?.location?.name ||
                                    (photo?.location?.address && (
                                       <Typography
                                          variant='body2'
                                          color='text.secondary'
                                          sx={{ mb: 0.5, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                       >
                                          <LocationOnIcon sx={{ mr: 0.5, fontSize: isMobile ? '0.9rem' : '1rem' }} />{' '}
                                          {photo.location.name || photo.location.address}
                                       </Typography>
                                    ))}
                                 <Typography variant='subtitle1' noWrap sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                                    {photo?.title}
                                 </Typography>
                                 {photo?.takenAt && (
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{ mb: isMobile ? 0.5 : 1, fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                                    >
                                       {photo?.takenAt?.toLocaleDateString()}
                                    </Typography>
                                 )}

                                 {photo?.tags?.length > 0 && (
                                    <Box
                                       sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}
                                    >
                                       {photo.tags.slice(0, 3).map((tag) => (
                                          <Chip
                                             key={tag}
                                             label={tag}
                                             size='small'
                                             variant='outlined'
                                          />
                                       ))}
                                       {photo.tags.length > 3 && (
                                          <Chip
                                             label={`+${photo.tags.length - 3}`}
                                             size='small'
                                             variant='outlined'
                                          />
                                       )}
                                    </Box>
                                 )}
                              </CardContent>
                              <CardActions sx={{ justifyContent: 'space-between' }}>
                                 <Button
                                    size='small'
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEditClick(photo)}
                                    color='primary'
                                 >
                                    Edit
                                 </Button>
                                 <Button
                                    size='small'
                                    color='error'
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteClick(photo)}
                                 >
                                    Delete
                                 </Button>
                              </CardActions>
                           </Card>
                        </Grid>
                     ))}
                  </Grid>
               ) : photos.length === 0 ? (
                  <Box
                     sx={{
                        p: 4,
                        textAlign: 'center',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                     }}
                  >
                     <ImageIcon
                        sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }}
                     />
                     <Typography variant='h6' color='text.secondary' gutterBottom>
                        No Photos Found
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        Upload some photos or adjust your search filters
                     </Typography>
                  </Box>
               ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                     <CircularProgress />
                  </Box>
               )}
            </TabPanel>

            {/* Calendar View Tab */}
         <TabPanel value={currentTab} index={1}>
            <PhotoCalendarView
               photos={photos}
               trips={trips || []}
               events={events || []}
               onCreateEvent={handleCreateEventFromCalendar}
               onCreateTrip={handleCreateTripFromCalendar}
               loading={loading}
            />
         </TabPanel>

         <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
            <DialogTitle>Delete Photo</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Are you sure you want to delete this photo? This action cannot be undone.
               </DialogContentText>
               {photoToDelete && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                     <img
                        src={photoToDelete.thumbnailUrl}
                        alt={photoToDelete.title}
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                     />
                  </Box>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleDeleteCancel}>Cancel</Button>
               <Button
                  disabled={isDeleting}
                  onClick={() => handleDeleteConfirm(photoToDelete?.id || '')}
                  color='error'
               >
                  {isDeleting ? 'Deleting...' : 'Delete'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Photo Sync Dialog */}
         <PhotoSyncDialog
            open={photoSyncDialogOpen}
            onClose={() => {
               handleCloseSyncDialog();
               setIsSyncing(false);
               setQrCode('');
               setIsShowQrCode(false);
               setPhotoUploadUrl('');
            }}
            tabValue={syncTabValue}
            onTabChange={handleSyncTabChange}
            onGooglePhotosSync={handleGooglePhotosSync}
            onICloudSync={handleICloudSync}
            syncInProgress={false}
            syncError={null}
            isShowQrCode={isShowQrCode}
            qrCode={qrCode}
            selectedPickerPhotos={selectedPickerPhotos}
            resetSelectedPickerPhotos={() => setSelectedPickerPhotos([])}
            isSyncing={isSyncing}
            handleDeletePhoto={(id: string) => {
               setSelectedPickerPhotos(selectedPickerPhotos.filter((photo) => photo.id !== id));
            }}
            isUploading={isUploading}
            // handleSyncPhotoUpload={handleSyncPhotoUpload}
         />

         {/* Photo Sharing Modal */}
         <PhotoSharingModal
            open={sharingModalOpen}
            onClose={() => setSharingModalOpen(false)}
            entityType='album'
            entityId='personal-photos'
            entityTitle='Personal Photos'
            selectedPhotos={Array.from(selectedPhotos)
               .map((photoId) => {
                  const photo = photos.find((p) => p.id === photoId);
                  return photo
                     ? {
                          id: photo.id,
                          url: photo.url || photo.thumbnailUrl,
                          thumbnailUrl: photo.thumbnailUrl,
                          title: photo.title,
                          description: photo.description || '',
                          takenAt: photo.takenAt,
                          uploadedBy: currentUser?.uid || '',
                          userId: currentUser?.uid || '',
                          filename: photo.filename || '',
                          uploadedAt: photo.uploadedAt,
                          tags: photo.tags || [],
                          location: photo.location,
                          tripId: photo.tripId,
                          eventId: photo.eventId,
                          tripName: photo.tripName,
                          eventName: photo.eventName,
                       }
                     : null;
               })
               .filter(Boolean)}
            entityData={{
               id: 'personal-photos',
               createdBy: currentUser?.uid || '',
               userId: currentUser?.uid || '',
               members: [
                  {
                     userId: currentUser?.uid || '',
                     email: currentUser?.email || '',
                     role: 'owner',
                  },
               ],
            }}
            onSuccess={(result) => {
               setSnackbarMessage('Photos shared successfully!');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               setSelectedPhotos(new Set()); // Clear selection after sharing
            }}
         />

         {/* Snackbar for notifications */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
               {snackbarMessage}
            </Alert>
         </Snackbar>

         {/* Edit Photo Dialog */}
         <EditPhotoDialog
            open={editDialogOpen}
            photo={selectedPhoto}
            onClose={() => setEditDialogOpen(false)}
         />

         {/* Photo Gallery Popup */}
         <PhotoGalleryPopup
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            photos={photos}
            initialPhotoIndex={selectedPhotoIndex}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
         />
      </Box>
   );
};

export default UploadPhotos;
